import React, { useState, useEffect, useCallback } from 'react';
import useAuth from '../../hooks/useAuth';
import api from '../../services/api';
import CreateJobModal from './CreateJobModal';
import JobDetailsModal from './JobDetailsModal';
import InsightsTab from './InsightsTab';
import './Dashboard.css';

const WORKFLOW_STAGES = [
  'Order Received',
  'Art/Design',
  'Screen Setup',
  'Printing',
  'Ready for Pickup'
];

const PRIORITY_CONFIG = {
  rush: { color: '#ef4444', bg: '#fef2f2', label: 'Rush' },
  high: { color: '#f59e0b', bg: '#fffbeb', label: 'High' },
  normal: { color: '#3b82f6', bg: '#eff6ff', label: 'Normal' },
  low: { color: '#6b7280', bg: '#f9fafb', label: 'Low' }
};

const STATUS_CONFIG = {
  'Order Received': { color: '#6366f1', icon: '📥' },
  'Art/Design': { color: '#8b5cf6', icon: '🎨' },
  'Screen Setup': { color: '#ec4899', icon: '🖼️' },
  'Printing': { color: '#f59e0b', icon: '🖨️' },
  'Ready for Pickup': { color: '#22c55e', icon: '📦' }
};

const Dashboard = () => {
  const { hasPermission } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);

  // Filters
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState(null); // 'in_progress', 'due_soon', 'overdue', or null

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalJobs, setTotalJobs] = useState(0);
  const JOBS_PER_PAGE = 25;

  // Archive mode
  const [showArchived, setShowArchived] = useState(false);

  // View mode: 'list', 'kanban', or 'calendar'
  const [viewMode, setViewMode] = useState('list');

  // Calendar state
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [calendarCapacity, setCalendarCapacity] = useState({});
  const [workloadSummary, setWorkloadSummary] = useState(null);

  // Modals
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);

  // Mobile filters toggle
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // Check permissions
  const canWrite = hasPermission('kerrscreen:write');
  const canAdmin = hasPermission('kerrscreen:admin');

  // Fetch jobs
  const fetchJobs = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        archived: showArchived
      };

      // Only paginate in list view - kanban and calendar need all jobs
      if (viewMode === 'list') {
        params.page = currentPage;
        params.limit = JOBS_PER_PAGE;
      } else {
        params.limit = 500; // Fetch all for kanban/calendar
      }

      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterPriority !== 'all') params.priority = filterPriority;
      if (searchTerm) params.search = searchTerm;
      if (activeFilter) params.special_filter = activeFilter;

      const response = await api.get('/kerrscreen/jobs', { params });
      setJobs(response.data.jobs || []);
      setTotalPages(response.data.pagination?.totalPages || 1);
      setTotalJobs(response.data.pagination?.total || 0);
      setError(null);
    } catch (err) {
      console.error('Error fetching jobs:', err);
      setError(err.response?.data?.message || 'Failed to fetch jobs');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterPriority, searchTerm, currentPage, showArchived, activeFilter, viewMode]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get('/kerrscreen/stats');
      setStats(response.data.stats);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, []);

  // Fetch calendar capacity data
  const fetchCalendarCapacity = useCallback(async () => {
    if (viewMode !== 'calendar') return;

    try {
      // Get first and last day of current month view (including overflow days)
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth();
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);

      // Extend range to cover the full calendar grid
      const startDate = new Date(firstDay);
      startDate.setDate(startDate.getDate() - firstDay.getDay());
      const endDate = new Date(lastDay);
      endDate.setDate(endDate.getDate() + (6 - lastDay.getDay()));

      const [capacityRes, summaryRes] = await Promise.all([
        api.get('/kerrscreen/calendar-capacity', {
          params: {
            start_date: startDate.toISOString().split('T')[0],
            end_date: endDate.toISOString().split('T')[0]
          }
        }),
        api.get('/kerrscreen/workload-summary', {
          params: {
            start_date: firstDay.toISOString().split('T')[0],
            end_date: lastDay.toISOString().split('T')[0]
          }
        })
      ]);

      // Convert capacity array to object keyed by date (normalize to YYYY-MM-DD)
      const capacityMap = {};
      (capacityRes.data.capacity || []).forEach(day => {
        // Handle both ISO string (2026-03-03T00:00:00.000Z) and date string (2026-03-03)
        const dateKey = day.date.split('T')[0];
        capacityMap[dateKey] = day;
      });
      setCalendarCapacity(capacityMap);
      setWorkloadSummary(summaryRes.data.summary);
    } catch (err) {
      console.error('Error fetching calendar capacity:', err);
    }
  }, [viewMode, currentMonth]);

  useEffect(() => {
    fetchJobs();
    fetchStats();
  }, [fetchJobs, fetchStats]);

  // Fetch calendar capacity when in calendar view
  useEffect(() => {
    fetchCalendarCapacity();
  }, [fetchCalendarCapacity]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterStatus, filterPriority, searchTerm, showArchived, activeFilter]);

  // Handle stat card click
  const handleStatCardClick = (filterType) => {
    // If clicking the same filter, clear it
    if (activeFilter === filterType) {
      setActiveFilter(null);
      setFilterPriority('all');
    } else if (filterType === 'rush') {
      // Rush is a priority filter, not a special filter
      setActiveFilter(null);
      setFilterPriority('rush');
    } else if (filterType === 'total') {
      // Total shows all jobs
      setActiveFilter(null);
      setFilterPriority('all');
      setFilterStatus('all');
    } else {
      // Special filters: in_progress, due_soon, overdue
      setActiveFilter(filterType);
      setFilterPriority('all');
    }
    setShowArchived(false); // Switch to active view when clicking stats
  };

  // Toggle archive view
  const toggleArchiveView = () => {
    setShowArchived(!showArchived);
    setActiveFilter(null); // Clear special filters
    setFilterPriority('all');
    setFilterStatus('all');
  };

  // Handle job click
  const handleJobClick = (job) => {
    setSelectedJob(job);
    setIsDetailsModalOpen(true);
  };

  // Handle modal success
  const handleModalSuccess = () => {
    fetchJobs();
    fetchStats();
  };

  // Filter jobs by status for kanban
  const getJobsByStatus = (status) => {
    return jobs.filter(job => job.status === status);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  // Check if job is overdue
  const isOverdue = (job) => {
    if (!job.due_date || job.status === 'Ready for Pickup') return false;
    return new Date(job.due_date) < new Date();
  };

  // Get workflow progress percentage
  const getWorkflowProgress = (status) => {
    const index = WORKFLOW_STAGES.indexOf(status);
    return index >= 0 ? ((index + 1) / WORKFLOW_STAGES.length) * 100 : 0;
  };

  // Render priority badge
  const renderPriorityBadge = (priority) => {
    const config = PRIORITY_CONFIG[priority] || PRIORITY_CONFIG.normal;
    return (
      <span
        className="priority-badge"
        style={{
          backgroundColor: config.bg,
          color: config.color,
          borderColor: config.color
        }}
      >
        {config.label}
      </span>
    );
  };

  // Render status badge
  const renderStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || { color: '#6b7280', icon: '•' };
    return (
      <span
        className="status-badge"
        style={{ backgroundColor: config.color }}
      >
        <span className="status-icon">{config.icon}</span>
        <span className="status-text">{status}</span>
      </span>
    );
  };

  // Render compact status indicator for table
  const renderStatusIndicator = (status) => {
    const config = STATUS_CONFIG[status] || { color: '#6b7280', icon: '•' };
    return (
      <div className="status-indicator-wrapper">
        <span
          className="status-dot"
          style={{ backgroundColor: config.color }}
        />
        <span className="status-label">{status}</span>
      </div>
    );
  };

  // Render job card for kanban and calendar
  const renderJobCard = (job, compact = false) => {
    const priorityConfig = PRIORITY_CONFIG[job.priority] || PRIORITY_CONFIG.normal;
    const statusConfig = STATUS_CONFIG[job.status] || { color: '#6b7280' };

    return (
      <div
        key={job.id}
        className={`kanban-card ${isOverdue(job) ? 'overdue' : ''} ${compact ? 'compact' : ''} priority-${job.priority}`}
        onClick={() => handleJobClick(job)}
        style={{ '--priority-color': priorityConfig.color, '--status-color': statusConfig.color }}
      >
        <div className="card-priority-bar" style={{ backgroundColor: priorityConfig.color }} />
        <div className="card-content">
          <div className="card-header">
            <span className="job-number">{job.job_number}</span>
            {job.priority === 'rush' && <span className="rush-badge">RUSH</span>}
          </div>
          <div className="card-customer">{job.customer_name}</div>
          {!compact && job.school_name && (
            <div className="card-school">{job.school_name}</div>
          )}
          {!compact && job.job_description && (
            <div className="card-description">
              {job.job_description.length > 60
                ? job.job_description.substring(0, 60) + '...'
                : job.job_description}
            </div>
          )}
          <div className="card-footer">
            <div className="card-meta">
              {job.due_date && (
                <span className={`due-date ${isOverdue(job) ? 'overdue' : ''}`}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                    <line x1="16" y1="2" x2="16" y2="6"/>
                    <line x1="8" y1="2" x2="8" y2="6"/>
                    <line x1="3" y1="10" x2="21" y2="10"/>
                  </svg>
                  {formatDate(job.due_date)}
                </span>
              )}
              {job.quantity > 0 && (
                <span className="quantity">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
                    <line x1="3" y1="6" x2="21" y2="6"/>
                  </svg>
                  {job.quantity}
                </span>
              )}
            </div>
            {!compact && (
              <div className="card-progress">
                <div
                  className="progress-bar"
                  style={{ width: `${getWorkflowProgress(job.status)}%`, backgroundColor: statusConfig.color }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Render kanban column
  const renderKanbanColumn = (status) => {
    const columnJobs = getJobsByStatus(status);
    const config = STATUS_CONFIG[status] || { color: '#6b7280', icon: '•' };

    return (
      <div key={status} className="kanban-column">
        <div className="column-header">
          <div className="column-header-left">
            <span className="column-icon" style={{ backgroundColor: config.color }}>
              {config.icon}
            </span>
            <h3>{status}</h3>
          </div>
          <span className="count">{columnJobs.length}</span>
        </div>
        <div className="column-content">
          {columnJobs.length === 0 ? (
            <div className="column-empty">No jobs</div>
          ) : (
            columnJobs.map(job => renderJobCard(job))
          )}
        </div>
      </div>
    );
  };

  // Calendar helpers
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    return { daysInMonth, startingDay, year, month };
  };

  const getJobsForDate = (year, month, day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return jobs.filter(job => {
      if (!job.due_date) return false;
      return job.due_date.split('T')[0] === dateStr;
    });
  };

  const navigateMonth = (direction) => {
    setCurrentMonth(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + direction);
      return newDate;
    });
  };

  const goToToday = () => {
    setCurrentMonth(new Date());
  };

  const isToday = (year, month, day) => {
    const today = new Date();
    return today.getFullYear() === year &&
           today.getMonth() === month &&
           today.getDate() === day;
  };

  // Render calendar view
  const renderCalendar = () => {
    const { daysInMonth, startingDay, year, month } = getDaysInMonth(currentMonth);
    const monthName = currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    const days = [];
    const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    // Empty cells for days before the first of the month
    for (let i = 0; i < startingDay; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dayJobs = getJobsForDate(year, month, day);
      const hasJobs = dayJobs.length > 0;
      const hasOverdue = dayJobs.some(j => isOverdue(j));
      const hasRush = dayJobs.some(j => j.priority === 'rush');

      // Get capacity data for this day
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const capacityData = calendarCapacity[dateStr];
      const capacityLevel = capacityData?.capacityLevel || (hasJobs ? 'light' : '');

      days.push(
        <div
          key={day}
          className={`calendar-day ${isToday(year, month, day) ? 'today' : ''} ${hasJobs ? 'has-jobs' : ''} ${hasOverdue ? 'has-overdue' : ''} ${hasRush ? 'has-rush' : ''} capacity-${capacityLevel}`}
        >
          <div className="day-header">
            <span className="day-number">{day}</span>
            {hasJobs && <span className="job-count">{dayJobs.length}</span>}
          </div>
          <div className="day-jobs">
            {dayJobs.slice(0, 3).map(job => {
              const statusConfig = STATUS_CONFIG[job.status] || { color: '#6b7280' };
              return (
                <div
                  key={job.id}
                  className={`calendar-job-item priority-${job.priority}`}
                  onClick={() => handleJobClick(job)}
                  style={{ '--status-color': statusConfig.color }}
                >
                  <span className="job-status-dot" style={{ backgroundColor: statusConfig.color }} />
                  <span className="job-customer">{job.customer_name}</span>
                  {job.priority === 'rush' && <span className="rush-indicator">!</span>}
                </div>
              );
            })}
            {dayJobs.length > 3 && (
              <div className="more-jobs">+{dayJobs.length - 3} more</div>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="calendar-container">
        <div className="calendar-header">
          <div className="calendar-nav">
            <button className="calendar-nav-btn" onClick={() => navigateMonth(-1)} title="Previous month">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="15 18 9 12 15 6"/>
              </svg>
            </button>
            <h3 className="calendar-month">{monthName}</h3>
            <button className="calendar-nav-btn" onClick={() => navigateMonth(1)} title="Next month">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="9 18 15 12 9 6"/>
              </svg>
            </button>
          </div>
          <button className="calendar-today-btn" onClick={goToToday}>
            Today
          </button>
        </div>

        {/* Workload Summary Bar */}
        {workloadSummary && (
          <div className="calendar-workload-summary">
            <div className="workload-stat">
              <span className="workload-value">{workloadSummary.total_jobs || 0}</span>
              <span className="workload-label">Jobs This Month</span>
            </div>
            <div className="workload-stat">
              <span className="workload-value">{workloadSummary.active_jobs || 0}</span>
              <span className="workload-label">Active</span>
            </div>
            {(workloadSummary.rush_jobs > 0 || workloadSummary.high_priority_jobs > 0) && (
              <div className="workload-stat priority">
                <span className="workload-value">{(workloadSummary.rush_jobs || 0) + (workloadSummary.high_priority_jobs || 0)}</span>
                <span className="workload-label">Rush/High Priority</span>
              </div>
            )}
            {workloadSummary.overdue_jobs > 0 && (
              <div className="workload-stat overdue">
                <span className="workload-value">{workloadSummary.overdue_jobs}</span>
                <span className="workload-label">Overdue</span>
              </div>
            )}
            <div className="workload-stat">
              <span className="workload-value">{workloadSummary.total_items || 0}</span>
              <span className="workload-label">Total Items</span>
            </div>
          </div>
        )}

        <div className="calendar-grid">
          {weekDays.map(day => (
            <div key={day} className="calendar-weekday">{day}</div>
          ))}
          {days}
        </div>
        <div className="calendar-legend">
          <div className="legend-item">
            <span className="legend-dot" style={{ backgroundColor: '#6366f1' }} />
            <span>In Progress</span>
          </div>
          <div className="legend-item">
            <span className="legend-dot" style={{ backgroundColor: '#22c55e' }} />
            <span>Ready</span>
          </div>
          <div className="legend-item">
            <span className="legend-dot rush" />
            <span>Rush</span>
          </div>
          <div className="legend-divider" />
          <div className="legend-item">
            <span className="capacity-indicator light" />
            <span>Light (1-3)</span>
          </div>
          <div className="legend-item">
            <span className="capacity-indicator moderate" />
            <span>Busy (4-6)</span>
          </div>
          <div className="legend-item">
            <span className="capacity-indicator heavy" />
            <span>Heavy (7+)</span>
          </div>
        </div>
      </div>
    );
  };

  // Render mobile card view for list
  const renderMobileCard = (job) => {
    const priorityConfig = PRIORITY_CONFIG[job.priority] || PRIORITY_CONFIG.normal;
    const statusConfig = STATUS_CONFIG[job.status] || { color: '#6b7280' };

    return (
      <div
        key={job.id}
        className={`mobile-job-card ${isOverdue(job) ? 'overdue' : ''}`}
        onClick={() => handleJobClick(job)}
        style={{ '--status-color': statusConfig.color }}
      >
        <div className="mobile-card-accent" style={{ backgroundColor: statusConfig.color }} />
        <div className="mobile-card-content">
          <div className="mobile-card-header">
            <span className="job-number">{job.job_number}</span>
            <div className="mobile-badges">
              {renderPriorityBadge(job.priority)}
            </div>
          </div>
          <div className="mobile-card-body">
            <div className="mobile-customer">{job.customer_name}</div>
            {job.school_name && <div className="mobile-school">{job.school_name}</div>}
            {job.job_description && (
              <div className="mobile-description">
                {job.job_description.length > 80
                  ? job.job_description.substring(0, 80) + '...'
                  : job.job_description}
              </div>
            )}
          </div>
          <div className="mobile-card-footer">
            <div className="mobile-status">
              <span className="status-dot" style={{ backgroundColor: statusConfig.color }} />
              <span>{job.status}</span>
            </div>
            <div className="mobile-meta">
              {job.due_date && (
                <span className={`mobile-due ${isOverdue(job) ? 'overdue' : ''}`}>
                  {formatDate(job.due_date)}
                </span>
              )}
              {job.quantity > 0 && <span className="mobile-qty">{job.quantity} pcs</span>}
            </div>
          </div>
          <div className="mobile-progress-bar">
            <div
              className="mobile-progress-fill"
              style={{ width: `${getWorkflowProgress(job.status)}%`, backgroundColor: statusConfig.color }}
            />
          </div>
        </div>
      </div>
    );
  };

  // Stat card component
  const StatCard = ({ title, value, icon, color, trend, onClick, isActive }) => (
    <div
      className={`stat-card ${onClick ? 'clickable' : ''} ${isActive ? 'active' : ''}`}
      style={{ '--accent-color': color }}
      onClick={onClick}
    >
      <div className="stat-icon" style={{ backgroundColor: `${color}15`, color }}>
        {icon}
      </div>
      <div className="stat-content">
        <span className="stat-label">{title}</span>
        <span className="stat-value" style={{ color }}>{value}</span>
        {trend && <span className="stat-trend">{trend}</span>}
      </div>
      {isActive && <div className="stat-active-indicator" style={{ backgroundColor: color }} />}
    </div>
  );

  return (
    <div className="kerrscreen-dashboard">
      {/* Page Header */}
      <div className="page-header">
        <div className="page-header-content">
          <div className="page-title-section">
            <div className="page-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="6" y="3" width="12" height="18" rx="1"/>
                <line x1="9" y1="7" x2="15" y2="7"/>
                <line x1="9" y1="11" x2="15" y2="11"/>
                <line x1="9" y1="15" x2="13" y2="15"/>
              </svg>
            </div>
            <div>
              <h1>Production Dashboard</h1>
              <p className="page-subtitle">Screen printing job management and tracking</p>
            </div>
          </div>
          <div className="page-actions">
            {canWrite && (
              <button
                className="btn-primary"
                onClick={() => setIsCreateModalOpen(true)}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="12" y1="5" x2="12" y2="19"/>
                  <line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
                <span>New Job</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Stats Section */}
      {stats && (
        <div className="stats-section">
          <div className="stats-grid">
            <StatCard
              title="Total Jobs"
              value={stats.total}
              icon={
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                </svg>
              }
              color="#6366f1"
              onClick={() => handleStatCardClick('total')}
              isActive={!activeFilter && filterPriority === 'all' && !showArchived}
            />
            <StatCard
              title="In Progress"
              value={stats.inProgress || (stats.total - (stats.byStatus?.['Ready for Pickup'] || 0))}
              icon={
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12 6 12 12 16 14"/>
                </svg>
              }
              color="#3b82f6"
              onClick={() => handleStatCardClick('in_progress')}
              isActive={activeFilter === 'in_progress'}
            />
            <StatCard
              title="Due Soon"
              value={stats.dueSoon}
              icon={
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                  <line x1="12" y1="9" x2="12" y2="13"/>
                  <line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
              }
              color="#f59e0b"
              onClick={() => handleStatCardClick('due_soon')}
              isActive={activeFilter === 'due_soon'}
            />
            <StatCard
              title="Overdue"
              value={stats.overdue}
              icon={
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="15" y1="9" x2="9" y2="15"/>
                  <line x1="9" y1="9" x2="15" y2="15"/>
                </svg>
              }
              color="#ef4444"
              onClick={() => handleStatCardClick('overdue')}
              isActive={activeFilter === 'overdue'}
            />
            <StatCard
              title="Rush Orders"
              value={stats.byPriority?.rush || 0}
              icon={
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
                </svg>
              }
              color="#dc2626"
              onClick={() => handleStatCardClick('rush')}
              isActive={filterPriority === 'rush' && !activeFilter}
            />
            <StatCard
              title="Archived"
              value={stats.archived || 0}
              icon={
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="21 8 21 21 3 21 3 8"/>
                  <rect x="1" y="3" width="22" height="5"/>
                  <line x1="10" y1="12" x2="14" y2="12"/>
                </svg>
              }
              color="#64748b"
              onClick={toggleArchiveView}
              isActive={showArchived}
            />
          </div>
        </div>
      )}

      {/* Controls Section */}
      <div className="controls-section">
        <div className="controls-row">
          {/* View Toggle */}
          <div className="view-toggle-container">
            <div className="view-toggle">
              <button
                className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
                onClick={() => setViewMode('list')}
                title="List View"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="8" y1="6" x2="21" y2="6"/>
                  <line x1="8" y1="12" x2="21" y2="12"/>
                  <line x1="8" y1="18" x2="21" y2="18"/>
                  <line x1="3" y1="6" x2="3.01" y2="6"/>
                  <line x1="3" y1="12" x2="3.01" y2="12"/>
                  <line x1="3" y1="18" x2="3.01" y2="18"/>
                </svg>
                <span className="view-label">List</span>
              </button>
              <button
                className={`view-btn ${viewMode === 'kanban' ? 'active' : ''}`}
                onClick={() => setViewMode('kanban')}
                title="Kanban Board"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="5" height="18" rx="1"/>
                  <rect x="10" y="3" width="5" height="12" rx="1"/>
                  <rect x="17" y="3" width="5" height="15" rx="1"/>
                </svg>
                <span className="view-label">Kanban</span>
              </button>
              <button
                className={`view-btn ${viewMode === 'calendar' ? 'active' : ''}`}
                onClick={() => setViewMode('calendar')}
                title="Calendar View"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                  <line x1="16" y1="2" x2="16" y2="6"/>
                  <line x1="8" y1="2" x2="8" y2="6"/>
                  <line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
                <span className="view-label">Calendar</span>
              </button>
              <button
                className={`view-btn ${viewMode === 'insights' ? 'active' : ''}`}
                onClick={() => setViewMode('insights')}
                title="Production Insights"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="20" x2="18" y2="10"/>
                  <line x1="12" y1="20" x2="12" y2="4"/>
                  <line x1="6" y1="20" x2="6" y2="14"/>
                </svg>
                <span className="view-label">Insights</span>
              </button>
            </div>
          </div>

          {/* Mobile Filter Toggle */}
          <button
            className="mobile-filter-toggle"
            onClick={() => setShowMobileFilters(!showMobileFilters)}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
            </svg>
            <span>Filters</span>
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              style={{ transform: showMobileFilters ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
            >
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </button>

          {/* Filters - Desktop */}
          <div className={`filters-container ${showMobileFilters ? 'show' : ''}`}>
            <div className="search-wrapper">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/>
                <line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                type="text"
                placeholder="Search jobs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
            <div className="filter-group">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="filter-select"
              >
                <option value="all">All Status</option>
                {WORKFLOW_STAGES.map(stage => (
                  <option key={stage} value={stage}>{stage}</option>
                ))}
              </select>
              <select
                value={filterPriority}
                onChange={(e) => setFilterPriority(e.target.value)}
                className="filter-select"
              >
                <option value="all">All Priority</option>
                <option value="rush">Rush</option>
                <option value="high">High</option>
                <option value="normal">Normal</option>
                <option value="low">Low</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="main-content">
        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner" />
            <p>Loading jobs...</p>
          </div>
        ) : error ? (
          <div className="error-state">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="12" cy="12" r="10"/>
              <line x1="15" y1="9" x2="9" y2="15"/>
              <line x1="9" y1="9" x2="15" y2="15"/>
            </svg>
            <h3>Error Loading Jobs</h3>
            <p>{error}</p>
            <button onClick={fetchJobs} className="btn-retry">Try Again</button>
          </div>
        ) : viewMode === 'list' ? (
          /* List View */
          <>
            {/* Archive Banner */}
            {showArchived && (
              <div className="archive-banner">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="21 8 21 21 3 21 3 8"/>
                  <rect x="1" y="3" width="22" height="5"/>
                  <line x1="10" y1="12" x2="14" y2="12"/>
                </svg>
                <span>Viewing Archived Jobs</span>
                <button className="btn-exit-archive" onClick={toggleArchiveView}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                  Exit Archive View
                </button>
              </div>
            )}

            {/* Active Filter Banner */}
            {activeFilter && (
              <div className="filter-banner">
                <span>
                  Showing: <strong>
                    {activeFilter === 'in_progress' && 'In Progress Jobs'}
                    {activeFilter === 'due_soon' && 'Jobs Due Within 7 Days'}
                    {activeFilter === 'overdue' && 'Overdue Jobs'}
                  </strong>
                </span>
                <button className="btn-clear-filter" onClick={() => setActiveFilter(null)}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                  Clear Filter
                </button>
              </div>
            )}

            {/* Rush Filter Banner */}
            {filterPriority === 'rush' && !activeFilter && (
              <div className="filter-banner rush">
                <span>Showing: <strong>Rush Orders Only</strong></span>
                <button className="btn-clear-filter" onClick={() => setFilterPriority('all')}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                  Clear Filter
                </button>
              </div>
            )}

            {/* Desktop Table */}
            <div className="table-container desktop-only">
              {jobs.length === 0 ? (
                <div className="empty-state">
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                    <line x1="12" y1="18" x2="12" y2="12"/>
                    <line x1="9" y1="15" x2="15" y2="15"/>
                  </svg>
                  <h3>No Jobs Found</h3>
                  <p>Get started by creating your first print job</p>
                  {canWrite && (
                    <button className="btn-primary" onClick={() => setIsCreateModalOpen(true)}>
                      Create Job
                    </button>
                  )}
                </div>
              ) : (
                <table className="jobs-table">
                  <thead>
                    <tr>
                      <th>Job</th>
                      <th>Customer</th>
                      <th>Description</th>
                      <th>Qty</th>
                      <th>Status</th>
                      <th>Priority</th>
                      <th>Due Date</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {jobs.map(job => (
                      <tr
                        key={job.id}
                        className={`${isOverdue(job) ? 'overdue-row' : ''} ${job.priority === 'rush' ? 'rush-row' : ''}`}
                        onClick={() => handleJobClick(job)}
                      >
                        <td>
                          <span className="job-number-cell">{job.job_number}</span>
                        </td>
                        <td>
                          <div className="customer-cell">
                            <span className="customer-name">{job.customer_name}</span>
                            {job.school_name && (
                              <span className="school-name">{job.school_name}</span>
                            )}
                          </div>
                        </td>
                        <td>
                          <span className="description-cell">
                            {job.job_description
                              ? job.job_description.length > 50
                                ? job.job_description.substring(0, 50) + '...'
                                : job.job_description
                              : '-'}
                          </span>
                        </td>
                        <td>
                          <span className="quantity-cell">{job.quantity || '-'}</span>
                        </td>
                        <td>{renderStatusIndicator(job.status)}</td>
                        <td>{renderPriorityBadge(job.priority)}</td>
                        <td>
                          <span className={`date-cell ${isOverdue(job) ? 'overdue' : ''}`}>
                            {formatDate(job.due_date)}
                          </span>
                        </td>
                        <td>
                          <button className="btn-view" onClick={(e) => { e.stopPropagation(); handleJobClick(job); }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                              <circle cx="12" cy="12" r="3"/>
                            </svg>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Mobile Card List */}
            <div className="mobile-card-list mobile-only">
              {jobs.length === 0 ? (
                <div className="empty-state">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                  </svg>
                  <h3>No Jobs Found</h3>
                  <p>{canWrite && 'Tap the button above to create one'}</p>
                </div>
              ) : (
                jobs.map(job => renderMobileCard(job))
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination-container">
                <div className="pagination-info">
                  Showing {((currentPage - 1) * JOBS_PER_PAGE) + 1} - {Math.min(currentPage * JOBS_PER_PAGE, totalJobs)} of {totalJobs} jobs
                </div>
                <div className="pagination-controls">
                  <button
                    className="pagination-btn"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    title="First page"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="11 17 6 12 11 7"/>
                      <polyline points="18 17 13 12 18 7"/>
                    </svg>
                  </button>
                  <button
                    className="pagination-btn"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    title="Previous page"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="15 18 9 12 15 6"/>
                    </svg>
                  </button>
                  <div className="pagination-pages">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      return (
                        <button
                          key={pageNum}
                          className={`pagination-page ${currentPage === pageNum ? 'active' : ''}`}
                          onClick={() => setCurrentPage(pageNum)}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    className="pagination-btn"
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    title="Next page"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="9 18 15 12 9 6"/>
                    </svg>
                  </button>
                  <button
                    className="pagination-btn"
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                    title="Last page"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="13 17 18 12 13 7"/>
                      <polyline points="6 17 11 12 6 7"/>
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </>
        ) : viewMode === 'kanban' ? (
          /* Kanban View */
          <>
            {/* Archive Banner */}
            {showArchived && (
              <div className="archive-banner">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="21 8 21 21 3 21 3 8"/>
                  <rect x="1" y="3" width="22" height="5"/>
                  <line x1="10" y1="12" x2="14" y2="12"/>
                </svg>
                <span>Viewing Archived Jobs</span>
                <button className="btn-exit-archive" onClick={toggleArchiveView}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                  Exit Archive View
                </button>
              </div>
            )}

            {/* Active Filter Banner */}
            {activeFilter && (
              <div className="filter-banner">
                <span>
                  Showing: <strong>
                    {activeFilter === 'in_progress' && 'In Progress Jobs'}
                    {activeFilter === 'due_soon' && 'Jobs Due Within 7 Days'}
                    {activeFilter === 'overdue' && 'Overdue Jobs'}
                  </strong>
                </span>
                <button className="btn-clear-filter" onClick={() => setActiveFilter(null)}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                  Clear Filter
                </button>
              </div>
            )}

            {/* Rush Filter Banner */}
            {filterPriority === 'rush' && !activeFilter && (
              <div className="filter-banner rush">
                <span>Showing: <strong>Rush Orders Only</strong></span>
                <button className="btn-clear-filter" onClick={() => setFilterPriority('all')}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                  Clear Filter
                </button>
              </div>
            )}

            <div className="kanban-board">
              {WORKFLOW_STAGES.map(status => renderKanbanColumn(status))}
            </div>
          </>
        ) : viewMode === 'calendar' ? (
          /* Calendar View */
          <>
            {/* Archive Banner */}
            {showArchived && (
              <div className="archive-banner">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="21 8 21 21 3 21 3 8"/>
                  <rect x="1" y="3" width="22" height="5"/>
                  <line x1="10" y1="12" x2="14" y2="12"/>
                </svg>
                <span>Viewing Archived Jobs</span>
                <button className="btn-exit-archive" onClick={toggleArchiveView}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                  Exit Archive View
                </button>
              </div>
            )}

            {/* Active Filter Banner */}
            {activeFilter && (
              <div className="filter-banner">
                <span>
                  Showing: <strong>
                    {activeFilter === 'in_progress' && 'In Progress Jobs'}
                    {activeFilter === 'due_soon' && 'Jobs Due Within 7 Days'}
                    {activeFilter === 'overdue' && 'Overdue Jobs'}
                  </strong>
                </span>
                <button className="btn-clear-filter" onClick={() => setActiveFilter(null)}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                  Clear Filter
                </button>
              </div>
            )}

            {/* Rush Filter Banner */}
            {filterPriority === 'rush' && !activeFilter && (
              <div className="filter-banner rush">
                <span>Showing: <strong>Rush Orders Only</strong></span>
                <button className="btn-clear-filter" onClick={() => setFilterPriority('all')}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                  Clear Filter
                </button>
              </div>
            )}

            {renderCalendar()}
          </>
        ) : viewMode === 'insights' ? (
          /* Insights View */
          <InsightsTab />
        ) : null}
      </div>

      {/* Create Job Modal */}
      <CreateJobModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={() => {
          setIsCreateModalOpen(false);
          handleModalSuccess();
        }}
      />

      {/* Job Details Modal */}
      <JobDetailsModal
        job={selectedJob}
        isOpen={isDetailsModalOpen}
        onClose={() => {
          setIsDetailsModalOpen(false);
          setSelectedJob(null);
        }}
        onSuccess={handleModalSuccess}
        canAdmin={canAdmin}
        workflowStages={WORKFLOW_STAGES}
      />
    </div>
  );
};

export default Dashboard;
